import type { MultiPolygon } from 'geojson';

import type { FindingStatsForImage } from '../../@types/contracts';
import type {
  BoundingBox,
  CornerstoneAnnotationFindingTypes,
  TImagetype,
} from '../../@types/global';
import type { ImageDimension } from '../../@types/inference-server-types';
import type { LegacyDepth } from '../../@types/latestImages';
import type { TCariesDepth } from '../../react-query/useSegmentations';

/**
 * {@link https://github.com/videahealth/nucleus-api/blob/develop/src/analyze-image/analyze-image.dto.ts#L291-L338 BE types}
 */
export interface ImageObject {
  analysisState: string;
  detectedImageType?: TImagetype;
  modelHashes?: Record<string, string>;
  models: Models | null;
  imageManifestId?: number | null;
  findings?: Findings;
  videaErrorCode: any;
  videaError?: AnalyzeImageVideaError | null;
  imageKeys?: ImageKeys;
  objectId: string;
  imageId: string;
  imageMetadata: ImageMetadata;
  fileDateCreated: string;
  path?: string;
  originalPath?: string;
  sharpenedPath?: string;
  thumbnailLargePath?: string;
  thumbnailMediumPath?: string;
  thumbnailSmallPath?: string;
  fileName: string;
  patientInfo: PatientInfo;
  userAnnotations: any[];
  rotation: number;
  imageIndex: number;
  fileObject?: { base64Image: string };
  imageDimension?: ImageDimension;
  userModifications?: UserModificationFromToolbar;
  imageRerunAt?: string;
  imageGroup?: 'OTHER' | 'FMX';
  fmxIndex?: number;
  fmxPriority?: number;
  bwViewIndex?: number | null;
  bwViewPriority?: number | null;
  estimatedVisitId?: number;
  imageAcquiredAt?: Date;
  updatedAt?: string;
  imageKey?: string;
  analysisId?: number;
  createdAt?: string;
  photoView?: 'intraoral' | 'extraoral' | 'outOfScope' | null;
}
export interface AnalyzeImageVideaError {
  analysesFailed: FailedAnalysisType[];
  analysisErrorMessagingCode: AnalysisErrorMessagingCode[];
}

export enum AnalysisErrorMessagingCode {
  SUCCESS = 'SUCCESS',
  VCA_FAILED = 'VCA_FAILED',
  VDA_FAILED = 'VDA_FAILED',
  RBL_FAILED = 'RBL_FAILED',
  PA_FAILED = 'PA_FAILED',
  PRL_FAILED = 'PRL_FAILED',
  CALCULUS_FAILED = 'CALCULUS_FAILED',
  TDIS_FAILED = 'TDIS_FAILED',
  CARIES_ONLY = 'CARIES_ONLY',
  TWO_FAILURES = 'TWO_FAILURES',
  MORE_THAN_TWO_FAILURES = 'MORE_THAN_TWO_FAILURES',
  FAILED = 'FAILED',
}

export enum FailedAnalysisType {
  CARIES_BW = 'CARIES_BW',
  PERIAPICAL_RADIOLUCENCY = 'PERIAPICAL_RADIOLUCENCY',
  RADIOGRAPHIC_BONE_LOSS = 'RADIOGRAPHIC_BONE_LOSS',
  TOOTH_IDENTIFICATION = 'TOOTH_IDENTIFICATION',
  CARIES_PA = 'CARIES_PA',
  TOOTH_THIRDS = 'TOOTH_THIRDS',
  CALCULUS = 'CALCULUS',
  CARIES_SUBCLASSIFICATION = 'CARIES_SUBCLASSIFICATION',
  VIDEA_DENTAL_ASSIST_CARIES = 'VIDEA_DENTAL_ASSIST_CARIES',
  RESTORATIVE_ADJACENT_LUCENCY = 'RESTORATIVE_ADJACENT_LUCENCY',
}

export enum ModelHashes {
  CARIES_BW = 'CARIES_BW',
  CARIES_PA = 'CARIES_PA',
  PERIAPICAL_RADIOLUCENCY = 'PERIAPICAL_RADIOLUCENCY',
  RADIOGRAPHIC_BONE_LOSS = 'RADIOGRAPHIC_BONE_LOSS',
  TOOTH_IDENTIFICATION = 'TOOTH_IDENTIFICATION',
  TOOTH_THIRDS = 'TOOTH_THIRDS',
  CALCULUS = 'CALCULUS',
  VIDEA_DENTAL_ASSIST = 'VIDEA_DENTAL_ASSIST',
  RESTORATIVE_ADJACENT_LUCENCY = 'RESTORATIVE_ADJACENT_LUCENCY',
}
export interface Models {
  caries?: CariesResult;
  periapical_radiolucency?: PeriapicalRadiolucencyResult;
  tooth_identification?: ToothIdentificationResult;
  radiographic_boneloss?: RadiographicBonelossResult;
  calculus?: any;
  restorative_adjacent_lucency?: RestorativeAdjacentLucencyResult;
}

export interface CariesResult {
  findings?: BoundingBoxFinding[];
  modelId: string;
  modelState: string;
  vcaMessage: string;
  type?: string;
}

export interface RestorativeAdjacentLucencyResult {
  findings: BoundingBoxFinding[];
  modelId: string;
  modelState: string;
}

export interface BoundingBoxFinding extends BoundingBox {
  id: string;
  sourceImageId?: string;
  type?: string;
  rblType?: string;
  source?: string;
  createdByUsername?: string;
  userValidation?: 'Agree' | 'Disagree' | 'Monitor';
  isCaries?: boolean;
  modelType?: string | null;
  confidenceScore?: number | null;
  toothNumber?: number | string;
  depth?: LegacyDepth | TCariesDepth | null;
  outline?: MultiPolygon | null;
  bin?: number | null;
  binSource?: 'confidenceThreshold' | string | null;
}

export interface PeriapicalRadiolucencyResult {
  findings: BoundingBoxFinding[];
  modelId: string;
  modelState: string;
  modelType?: string | null;
}

export interface CalculusResult extends PeriapicalRadiolucencyResult {
  numberOfFindings: number;
}

export interface ToothIdentificationResult {
  findings: ToothIdentificationFinding[];
  modelId: string;
  modelState: string;
}

export interface ToothIdentificationFinding extends BoundingBox {
  id: string;
  toothNumber: number | string;
  confidenceScore?: number;
}

export interface RadiographicBonelossResult {
  findings?: RadiographicBonelossFinding[];
  modelId: string;
  modelState: string;
}

export interface RadiographicBonelossFinding {
  toothNumber: number | string;
  toothIdentifier?: string;
  sides: RBLSide[];
}

export interface RBLSide {
  side: string;
  distanceMM: number | null;
  points: RBLPoint[];
  userValidation?: 'Agree' | 'Disagree' | 'Monitor';
}

export interface RBLPoint {
  id: string;
  x?: number | null;
  y?: number | null;
  rblType: string;
}

/**
 * @deprecated
 */
export interface Findings {
  caries: CariesFinding[];
}

/**
 * @deprecated
 */
export interface CariesFinding extends BoundingBox {
  id: string;
}

export interface ImageKeys {
  s3path: string;
  original: ImageKey;
  sharpened: ImageKey;
  sharpest: ImageKey;
  enhanced: ImageKey;
  generalized: ImageKey;
}

export interface ImageKey {
  key: string;
  version: string;
}

export interface ImageMetadata {
  modality?: string;
  photometricInterpretation?: string;
  anatomicRegionSequenceKey?: string;
  anatomicRegionSequenceName: string;
  primaryAnatomicStructureSequenceKey?: string;
  primaryAnatomicStructureSequenceName?: string;
}

export interface PatientInfo {
  patientId: string;
  analysisDate: string;
  dataSource: string;
  displayed: boolean;
  estimatedVisitDate: string;
  examDate: string;
  id: string;
  latestVisitAnalyzedImagesCount: number;
  name: any;
}

export interface Patient {
  id: string;
  patientId: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  latestVisitAnalyzedImagesCount: number;
  estimatedVisitDate: string;
  dataSource: string;
  analysisDate: string;
  examDate: string;
  displayed: boolean;
  externalPatientId?: string;
  isSearchedPatient: boolean;
  images: {
    [key: string]: ImageObject;
  };
  findingStats?: FindingStatsForImage;
}

export type SliderRange = [number, number];

export type SliderMark = {
  value: number;
};

export type FindingTypes = {
  caries: boolean;
  incipients: boolean;
  periapical_radiolucency: boolean;
  radiographic_boneloss: boolean;
  user: boolean;
  area_of_concern: boolean;
  calculus: boolean;
  restorative_adjacent_lucency: boolean;
};

export type UserValidation = 'Agree' | 'Disagree' | 'Monitor';

export interface FindingObject extends BoundingBoxFinding, RBLSide {
  toothNumber: number | string;
  validation?: UserValidation;
  annotationId?: string; // for user annotations
}

type UserModificationFromToolbar = {
  brightnessValue: number;
  contrastValue: number;
  hqEnabled: boolean;
  opacityInverted: boolean;
  imageMirrored: boolean;
};

export type CornerstoneFriendlyAnnotation = BoundingBox & {
  active?: boolean;
  color?: string;
  spaceBetweenDashes?: number[];
  invalidated?: boolean;
  visible?: boolean;
  handles: {
    start: CornerstoneCoordinates;
    end: CornerstoneCoordinates;
    textBox: Record<string, never>;
  };
  type?: CornerstoneAnnotationFindingTypes;
};

export interface UserAnnotation extends CornerstoneFriendlyAnnotation {
  id: string;
  annotationId: string;
  imageId: string;
  sourceImageId: string;
  validation?: UserValidation;
  source: string;
}

export const getIsUserAnnotation = (
  finding: FindingObject | UserAnnotation,
): finding is UserAnnotation => finding.source === 'userAnnotations';

type CornerstoneCoordinates = {
  x: number;
  y: number;
};

export const FINDINGS_CATEGORIES = [
  'restorative',
  'incipients',
  'periodontal',
  'endodontic',
  'chart',
] as const;

export type FindingsCategory = (typeof FINDINGS_CATEGORIES)[number];
